import {
  useBillingBillingSettingsRetrieveQuery,
  useInvoicesInvoiceEmailCreateMutation,
} from '@bambi/api-client/bambi';
import type { Invoice, InvoiceEmail } from '@bambi/api-client/schemas';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import { ContextFormFieldEmail } from '@bambi/component-library/src/components/forms/context/ContextFormFieldEmail';
import { ContextFormFieldSelect } from '@bambi/component-library/src/components/forms/context/ContextFormFieldSelect';
import { ContextFormFieldText } from '@bambi/component-library/src/components/forms/context/ContextFormFieldText';
import { ContextFormFieldTextArea } from '@bambi/component-library/src/components/forms/context/ContextFormFieldTextArea';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { ReloadIcon } from '@radix-ui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';

import { useExportInvoices } from '../../useExportInvoices';
import { usePayerEmail } from '../hooks/usePayerEmail';
import { usePaymentPayers } from '../hooks/usePaymentPayers';
import { useInvoiceBlobContext } from '../InvoiceBlobContext';
import type { InvoiceEmailData } from './invoiceEmailTemplateRenderer';
import { invoiceEmailTemplateRenderer } from './invoiceEmailTemplateRenderer';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { selectCurrentOrganization } from 'src/features/auth/selectors';
import { useHydratedInvoice } from 'src/features/billing/invoices/useHydratedInvoice';
import { INVOICE_EMAIL_TEMPLATE_DEFAULT } from 'src/features/billing/settings/constants';

type EmailFormValues = {
  payer?: SelectOption<string>;
  email_to: string;
  email_subject: string;
  email_body: string;
};

interface InvoiceEmailFormProps {
  invoice: Invoice;
  invoiceNumber: string;
  onClose: () => void;
}

export function InvoiceEmailForm({
  invoice,
  invoiceNumber,
  onClose,
}: InvoiceEmailFormProps) {
  const invoiceId = invoice.id;
  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const dispatch = useAppDispatch();
  const { isLoading: isLoadingBillingData, data: billingData } =
    useBillingBillingSettingsRetrieveQuery({});
  const [createEmail, { isLoading: isCreatingEmail }] =
    useInvoicesInvoiceEmailCreateMutation();
  const { isLoadingPayers, payerOptions, selfPayer } =
    usePaymentPayers(invoiceId);

  const { isLoading: isLoadingHydratedInvoice, data: hydratedInvoice } =
    useHydratedInvoice(invoice);
  const passengerId = useMemo(() => {
    if (isLoadingHydratedInvoice) return '';
    return (
      hydratedInvoice?.line_items.find(
        (lineItem) => lineItem.line_item_type === 'trip'
      )?.trip.passenger.id ?? ''
    );
  }, [isLoadingHydratedInvoice, hydratedInvoice]);

  // PDF attachment state
  const { handleExport, isLoading: isGeneratingPdf } =
    useExportInvoices('BLOB');
  const invoiceExportBlobUrl = useAppSelector(
    (state) => state.invoice.invoiceExportBlobUrl
  );
  const { blobUrlBlobMap, blobUrlUploadedMap, setBlobUrlUploadedMap } =
    useInvoiceBlobContext();
  const [attachedPdfBlob, setAttachedPdfBlob] = useState<Blob | undefined>();
  const [attachedPdfUrl, setAttachedPdfUrl] = useState<string | undefined>();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [hasReviewedPdf, setHasReviewedPdf] = useState(false);

  // Detect when a newly generated PDF blob is available; advance to review step
  useEffect(() => {
    if (!invoiceExportBlobUrl) return;
    if (blobUrlUploadedMap[invoiceExportBlobUrl]) return;

    const blob = blobUrlBlobMap[invoiceExportBlobUrl];
    if (!blob) return;

    setAttachedPdfBlob(blob);
    setAttachedPdfUrl(URL.createObjectURL(blob));
    setStep(2);
    setHasReviewedPdf(false);
    setBlobUrlUploadedMap((current) => ({
      ...current,
      [invoiceExportBlobUrl]: true,
    }));
  }, [
    invoiceExportBlobUrl,
    blobUrlBlobMap,
    blobUrlUploadedMap,
    setBlobUrlUploadedMap,
  ]);

  // Revoke the locally-created blob URL when it changes (regenerate) or on unmount
  useEffect(() => {
    return () => {
      if (attachedPdfUrl) {
        URL.revokeObjectURL(attachedPdfUrl);
      }
    };
  }, [attachedPdfUrl]);

  const templateData: InvoiceEmailData = useMemo(
    () => ({
      invoice_number: invoiceNumber,
      organization_name: currentOrganization?.name ?? '',
      billing_email: billingData?.email ?? '',
      billing_phone: billingData?.phone_number ?? '',
    }),
    [invoiceNumber, currentOrganization?.name, billingData]
  );

  const form = useForm<EmailFormValues>({
    reValidateMode: 'onBlur',
    defaultValues: {
      email_subject: `Invoice: ${invoiceNumber} from ${
        currentOrganization?.name ?? ''
      }`,
    },
  });

  // Determine payer type/ID and auto-populate email_to
  const selectedPayer = form.watch('payer');
  const payerType =
    selectedPayer?.value === selfPayer?.value
      ? 'passenger'
      : 'payer_organization';
  const payerId =
    selectedPayer?.value === selfPayer?.value
      ? passengerId
      : (selectedPayer?.value ?? '');
  const { email: payerEmail } = usePayerEmail(payerType, payerId);

  useEffect(() => {
    form.setValue('email_to', payerEmail ?? '');
  }, [payerEmail, form]);

  // Set rendered template body once billing data loads
  useEffect(() => {
    if (isLoadingBillingData || !billingData) {
      return;
    }

    form.setValue(
      'email_body',
      invoiceEmailTemplateRenderer(INVOICE_EMAIL_TEMPLATE_DEFAULT, templateData)
    );
  }, [isLoadingBillingData, billingData, form, templateData]);

  const handleSubmit = useCallback<SubmitHandler<EmailFormValues>>(
    async ({ payer: _payer, ...data }) => {
      try {
        let invoiceEmailPayload: InvoiceEmail | FormData;
        if (attachedPdfBlob) {
          const fd = new FormData();
          fd.append('invoice_id', invoiceId);
          fd.append('email_to', data.email_to);
          fd.append('email_subject', data.email_subject);
          fd.append('email_body', data.email_body);
          fd.append('invoice_file', attachedPdfBlob, `${invoiceId}.pdf`);
          invoiceEmailPayload = fd as unknown as InvoiceEmail;
        } else {
          invoiceEmailPayload = {
            invoice_id: invoiceId,
            message: {
              email_to: data.email_to,
              email_subject: data.email_subject,
              email_body: data.email_body,
            },
          };
        }

        await createEmail({
          invoiceEmail: invoiceEmailPayload,
        }).unwrap();

        dispatch(
          show({
            type: 'success',
            title: 'Successfully sent email',
          })
        );

        onClose();
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Failed to send email',
            description: formatServerError(e),
          })
        );
      }
    },
    [attachedPdfBlob, createEmail, dispatch, invoiceId, onClose]
  );

  if (isLoadingBillingData || isLoadingPayers || isLoadingHydratedInvoice) {
    return (
      <div className="flex flex-row justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  // Step 1: Generate the invoice PDF
  if (step === 1) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          Press Generate to create and review the invoice. You'll add the email
          details in the next step.
        </p>
        <div className="flex flex-row justify-between">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={isGeneratingPdf}
            onClick={() => handleExport('condensed', [invoiceId])}
          >
            Generate
          </Button>
        </div>
      </div>
    );
  }

  // Step 2: Review the generated PDF before proceeding
  if (step === 2) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          You must review the generated invoice before continuing.
        </p>
        <div className="text-center">
          <a
            href={attachedPdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
            onClick={() => setHasReviewedPdf(true)}
          >
            View Invoice PDF
          </a>
        </div>
        <div className="flex flex-row justify-between">
          <Button onClick={() => setStep(1)}>Back</Button>
          <SimpleTooltip
            trigger={
              <div>
                <Button
                  variant="primary"
                  disabled={!hasReviewedPdf}
                  onClick={() => setStep(3)}
                >
                  Next
                </Button>
              </div>
            }
            content={
              !hasReviewedPdf
                ? 'You must view the invoice before continuing'
                : ''
            }
          />
        </div>
      </div>
    );
  }

  // Step 3: Compose and send the email
  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center">
          <a
            className="border-none px-3 py-2 text-sm text-blue-600 underline"
            href={attachedPdfUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Attached Invoice PDF
          </a>
          <Button
            loading={isGeneratingPdf}
            onClick={() => handleExport('condensed', [invoiceId])}
            variant="icon"
          >
            <div
              className="flex flex-col items-center text-xs"
              style={{ fontSize: '0.625rem' }}
            >
              <ReloadIcon aria-label="Regenerate Invoice" />
              Regenerate
            </div>
          </Button>
        </div>
        <ContextFormFieldSelect<string>
          fieldPath="payer"
          label="Payer"
          options={payerOptions}
          placeholder="Select a payer"
        />
        <ContextFormFieldEmail
          fieldPath="email_to"
          label="To"
          rules={{ required: 'This field is required' }}
          helperText="Default value retrieved from payer. Use commas to separate multiple emails."
        />
        <ContextFormFieldText
          fieldPath="email_subject"
          label="Subject"
          rules={{ required: 'This field is required' }}
        />
        <ContextFormFieldTextArea
          fieldPath="email_body"
          label="Body"
          rules={{ required: 'This field is required' }}
        />
        <div className="flex flex-row justify-between">
          <Button onClick={() => setStep(2)}>Back</Button>
          <Button
            variant="primary"
            className="flex flex-row gap-2"
            loading={isCreatingEmail}
            onClick={form.handleSubmit(handleSubmit)}
          >
            <EnvelopeIcon className="h-[22px] w-[22px]" />
            Send Email
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}
